import React from "react";
import {
  Box,
  Container,
  Typography,
  Link,
  IconButton,
  Stack,
  Divider,
} from "@mui/material";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";

const footerLinks = {
  "QUICK LINKS": [
    { label: "Why I'm Running", href: "#why-running" },
    { label: "About Gary", href: "#about" },
    { label: "The Issues", href: "#issues" },
    { label: "Why Now", href: "#beliefs" },
    { label: "Families Like Ours", href: "#families" },
  ],
  "NEW FEATURES": [
    { label: "Sign the Petition", href: "#petition" },
    { label: "Events Calendar", href: "#events" },
    { label: "Photo Gallery", href: "#photos" },
    { label: "Endorsements", href: "#neighbors" },
    { label: "District 8 Legislation", href: "#legislation" },
    { label: "District Map", href: "#district-map" },
    { label: "In the News", href: "#news" },
    { label: "Your Vote", href: "#voting" },
  ],
  "TAKE ACTION": [
    { label: "Donate", href: "#support" },
    { label: "Volunteer", href: "#volunteer" },
    { label: "Request a Yard Sign", href: "#volunteer" },
    { label: "Pledge to Vote", href: "#voting" },
    { label: "Sign the Petition", href: "#petition" },
  ],
};

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const handleNavClick = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: "auto", block: "start" });
    }
  };

  return (
    <Box
      sx={{
        bgcolor: "#0a192f",
        color: "white",
        pt: 8,
        pb: 4,
        position: "relative",
      }}
    >
      <Container maxWidth="lg">
        {/* ── Main footer row ── */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: { md: "space-between" },
            alignItems: { xs: "center", md: "flex-start" },
            gap: { xs: 4, md: 3, lg: 4 },
            flexWrap: "wrap",
            mb: 6,
          }}
        >
          {/* Brand Column */}
          <Box
            sx={{
              textAlign: { xs: "center", md: "left" },
              maxWidth: { md: "240px", lg: "280px" },
              width: "100%",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                mb: 1,
                letterSpacing: 1,
                cursor: "pointer",
              }}
              onClick={(e) => handleNavClick(e, "#home")}
            >
              TAYLOR <span style={{ color: "#c49a3d" }}>FOR THE</span>
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                mb: 3,
                mt: -1,
                color: "#c49a3d",
                cursor: "pointer",
              }}
              onClick={(e) => handleNavClick(e, "#home")}
            >
              MIDDLETOWN
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.6)", mb: 4 }}
            >
              Bringing common-sense leadership to Middletown. Together, we can
              make it make sense.
            </Typography>

            <Stack
              spacing={1.5}
              sx={{ mb: 4, alignItems: { xs: "center", md: "flex-start" } }}
            >
              <Link
                href="mailto:friendsofgarytaylor@gmail.com"
                sx={{
                  color: "#3b82f6",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  textDecoration: "none",
                  fontSize: "0.9rem",
                  maxWidth: "100%",
                }}
              >
                <EmailIcon sx={{ fontSize: 18, flexShrink: 0 }} />
                <span style={{ wordBreak: "break-all" }}>
                  friendsofgarytaylor@gmail.com
                </span>
              </Link>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.6)",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontSize: "0.9rem",
                }}
              >
                <LocationOnIcon sx={{ fontSize: 18, color: "#d32f2f" }} />
                PO Box 161, Middletown, DE 19709
              </Typography>
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              sx={{ justifyContent: { xs: "center", md: "flex-start" } }}
            >
              {[
                {
                  Icon: FacebookIcon,
                  label: "Facebook",
                  href: "https://www.facebook.com/share/1DLn3h59a8/",
                },
                {
                  Icon: LinkedInIcon,
                  label: "LinkedIn",
                  href: "https://www.linkedin.com/in/gary-taylor-33270b425/",
                },
              ].map(({ Icon, label, href }) => (
                <IconButton
                  key={label}
                  aria-label={label}
                  {...(href
                    ? {
                        component: "a",
                        href,
                        target: "_blank",
                        rel: "noopener noreferrer",
                      }
                    : {})}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.05)",
                    color: "white",
                    borderRadius: 1,
                    "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
                  }}
                >
                  <Icon fontSize="small" />
                </IconButton>
              ))}
            </Stack>
          </Box>

          {/* Link Columns */}
          {Object.entries(footerLinks).map(([title, links]) => (
            <Box
              key={title}
              sx={{
                textAlign: { xs: "center", md: "left" },
                width: "100%",
                maxWidth: { md: "160px" },
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: "#c49a3d",
                  fontWeight: "bold",
                  mb: 2,
                  display: "block",
                  letterSpacing: 1.5,
                }}
              >
                {title}
              </Typography>
              <Stack
                spacing={1.5}
                sx={{ alignItems: { xs: "center", md: "flex-start" } }}
              >
                {links.map((link) => (
                  <Link
                    key={link.label}
                    href={link.href}
                    underline="hover"
                    onClick={(e) => handleNavClick(e, link.href)}
                    sx={{
                      color: "rgba(255,255,255,0.6)",
                      fontSize: "0.85rem",
                      "&:hover": { color: "white" },
                      cursor: "pointer",
                    }}
                  >
                    {link.label}
                  </Link>
                ))}
              </Stack>
            </Box>
          ))}
        </Box>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 3 }} />

        {/* Bottom Legal */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            textAlign: "center",
            gap: 2,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: "rgba(255,255,255,0.4)", letterSpacing: 0.5 }}
          >
            PAID FOR BY FRIENDS OF GARY TAYLOR · PO Box 161, Middletown, DE
            19709
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)" }}>
            © {currentYear} Friends of Gary Taylor. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
